import { debug as debugGen } from "debug";
import Subscriber from "./subscriber/index";
import Compose from "./compose/index";
import ResultUpdate from "./compose/ResultUpdate";
import CacheCompose from "./compose/CacheCompose";
import EventCompose, { PathNode } from "./compose/Compose";
import Transport from "./transport/index";
import EndHook from "./hook/end";
import pageManager, {
    CachePage,
    getTraceInfo,
    encodeTraceDetail
} from "./page";
import { getTrace } from "./store";
import { ActionItem, STORAGE_TRACE_KEY } from "./types";
import Expose from "./subscriber/plugins/expose";

const debug = debugGen("vtTracker:Tracker");

class Tracker {
    private subscriber: Subscriber;

    private compose: Compose;

    private transport: Transport;

    private resultUpdate: ResultUpdate;

    private endHook: EndHook;

    async init(): Promise<void> {
        // 取trace_detail作为最前置依赖，不然自动曝光的时候可能还没有渠道trace_detail
        pageManager.storeTrace = await getTrace(STORAGE_TRACE_KEY);
        pageManager.init();

        this.subscriber = new Subscriber();
        this.compose = new Compose();
        this.transport = new Transport();
        this.resultUpdate = new ResultUpdate();
        this.endHook = new EndHook();

        this.subscriber
            .pipe(this.compose)
            .pipe(this.transport)
            .pipe(this.endHook)
            .pipe(this.resultUpdate)
            .pipe(new CacheCompose(this.compose));

        this.subscriber.subscribe();

        // 自动进行页面曝光上报
        this.autoExpose();
    }

    autoExpose(): void {
        debug("auto expose for page number: %d", pageManager.pageNum);

        // 标注的页面自动走普通曝光上报
        if (pageManager.pageNum <= 1) {
            const cp: CachePage = pageManager.getPage(document.documentElement);

            const evt: Event = new Event("vtExpose", {
                bubbles: true
            });

            cp.node.dispatchEvent(evt);
        }
    }

    report(item: ActionItem): void {
        const cp: CachePage = pageManager.getPage(item.appEl);

        if (typeof cp !== "undefined") {
            if (!item.appid) {
                item.appid = cp.appid;
            }

            if (!item.page_id) {
                item.page_id = cp.pageid;
            }
        }

        if (this.compose) {
            this.compose.add(item);
        }
    }

    /**
     * 将外部输入的上报内容转换成强类型的ActionItem
     * @param  {any     = {}}        input [description]
     * @return {ActionItem}   [description]
     */
    static parse2ActionItem(input: any = {}): ActionItem {
        // eslint-disable-line @typescript-eslint/no-explicit-any
        return {
            appEl: input.appEl || document.documentElement,
            targetEl: input.targetEl || null,
            appid: String(input.appid || ""),
            page_id: String(input.page_id || ""),
            item_id: String(input.item_id || ""),
            sub_item_id:
                (input.sub_item_id && String(input.sub_item_id).split(",")) ||
                [],
            module_id: String(input.module_id || ""),
            sub_module_id: String(input.sub_module_id || ""),
            position_id: String(input.position_id || ""),
            test_id: parseInt(input.test_id, 10) || 0,
            rule_id: parseInt(input.rule_id, 10) || 0,
            str_ruleid: String(input.str_ruleid || ""),
            action_id: parseInt(input.action_id, 10) || 0,
            action_value: parseInt(input.action_value, 10) || 0,
            action_attr: parseInt(input.action_attr, 10) || 0,
            item_type: String(input.item_type || ""),
            trigger_info: String(input.trigger_info || ""),
            busi_info: String(input.busi_info || "")
        };
    }

    /**
     * 外部通过api进行上报
     * @param {[type]} input 上报内容
     */
    reportAlgorithm(input: any): void {
        // eslint-disable-line @typescript-eslint/no-explicit-any
        if (Array.isArray(input)) {
            input.forEach(it => this.report(Tracker.parse2ActionItem(it)));
        } else {
            this.report(Tracker.parse2ActionItem(input));
        }
    }

    getUrl(url: string, detail: string | Element): string {
        const u = new URL(url);

        if (typeof detail === "string") {
            const cp: CachePage = pageManager.getPage(document.documentElement);

            const trace = getTraceInfo(cp);

            u.searchParams.set("traceNum", String(trace.traceNum));
            u.searchParams.set("traceId", trace.traceId);
            u.searchParams.set("traceIndex", trace.traceIndex);
            u.searchParams.set("traceDetail", encodeTraceDetail(detail));
        } else if (detail instanceof Element) {
            const nodes: Array<PathNode> = [];

            let el: Element = detail;

            while (el instanceof Element) {
                const pn = EventCompose.vtNodeInfo(el);

                if (pn !== null) {
                    nodes.push(pn);

                    if (pn.attr.appid) {
                        break;
                    }
                }

                el = el.parentElement;
            }

            const { page, item } = EventCompose.pathNodeAction(
                EventCompose.fixNodePath(nodes).reverse()
            );

            const trace = getTraceInfo(page, item);

            u.searchParams.set("traceNum", String(trace.traceNum));
            u.searchParams.set("traceId", trace.traceId);
            u.searchParams.set("traceIndex", trace.traceIndex);
            u.searchParams.set(
                "traceDetail",
                encodeTraceDetail(trace.traceDetail)
            );
        }

        return u.href;
    }

    setAppid(id: string): void {
        const cp: CachePage = pageManager.getPage(document.documentElement);

        cp.appid = id;
    }

    getTraceIndex(): string {
        const cp: CachePage = pageManager.getPage(document.documentElement);

        return (cp.traceinfo && cp.traceinfo.trace_index) || "";
    }

    /**
     * 手动调用组件曝光
     * @param {Element} el [description]
     */
    expose(el: Element): Promise<void> {
        return el && el.dispatchEvent && Expose.dispatchExpose(el);
    }

    /**
     * 将某个元素添加到曝光监听中
     * @param {Element} el [description]
     */
    addExposeObserve(el: Element): void {
        this.subscriber.addExposeObserve(el);
    }

    isInObserve(el: Element): boolean {
        const list = this.subscriber.allObserved;

        return list.findIndex(obel => obel.isSameNode(el)) >= 0;
    }
}

export default Tracker;
